import React, { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { Panel, Pill } from "@/components/croc/ui";
import { fmtUsd } from "@/lib/format";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Clock, Gift, TrendingUp, TrendingDown, Layers } from "lucide-react";

export const PredictionSchedule = () => {
  const { address } = useWallet();
  const [searchParams] = useSearchParams();
  const highlightPkg = searchParams.get("pkg");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    if (!address) { setData(null); return; }
    api.predictionPackages(address).then(setData).catch(() => {});
  }, [address]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  // deep-link from the Command Center package card: scroll to + highlight that package
  useEffect(() => {
    if (!highlightPkg || !data?.packages?.length) return;
    const t = setTimeout(() => {
      const el = document.querySelector(`[data-testid="pred-package-${highlightPkg}"]`);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 300);
    return () => clearTimeout(t);
  }, [highlightPkg, data]);

  // nudge before an open, unanswered slot (across any package) closes so the day's bonus isn't lost
  useEffect(() => {
    if (!data?.packages || !address) return;
    for (const p of data.packages) {
      const soon = (p.slots || []).find((s) => s.status === "open" && !s.answered_side &&
        (new Date(s.closes) - Date.now()) < 60 * 60 * 1000);
      if (soon) {
        const mins = Math.max(1, Math.round((new Date(soon.closes) - Date.now()) / 60000));
        toast.warning(`${p.tier_name} prediction closing in ${mins}m — answer to keep this package's bonus`,
          { id: `pred-nudge-${p.stake_id}-${soon.slot}` });
      }
    }
  }, [data, address]);

  const minsLeft = (iso) => Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));

  const answer = async (pkg, slot, side) => {
    if (!address) return toast.error("Connect your wallet to predict");
    try {
      setBusy(`${pkg.stake_id}-${slot}-${side}`);
      await api.predictionAnswer({ address, tier: pkg.tier, slot, side, stake_id: pkg.stake_id });
      toast.success(`Prediction locked: ${side.toUpperCase()}`, { description: `${pkg.tier_name} package` });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(null); }
  };

  const packages = data?.packages ?? [];

  return (
    <Panel className="mb-6" data-testid="prediction-schedule">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-display font-bold text-white">Daily Predictions · Polymarket</div>
          <div className="text-xs text-muted-foreground">
            Each staking package has its own set of daily questions. Answer every question of a package to earn a bonus
            equal to THAT package's daily reward — paid with the 00:00 GST payout; miss one and that package's bonus is forfeited.
          </div>
        </div>
        {packages.length > 0 && (
          <Pill tone="gold" data-testid="prediction-total-bonus">
            <Gift className="w-3 h-3" /> Up to +{fmtUsd(data?.total_bonus_usd)}/day
          </Pill>
        )}
      </div>

      {!address && (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="pred-connect-note">
          Connect your wallet to see your packages' predictions.
        </div>
      )}

      {address && data?.questions_unavailable && (
        <div className="text-sm text-muted-foreground py-6 text-center">Loading live markets…</div>
      )}

      {address && !data?.questions_unavailable && packages.length === 0 && (
        <div className="text-sm text-muted-foreground py-6 text-center" data-testid="pred-no-packages">
          You have no active packages yet. Buy &amp; auto-stake a package to unlock its daily predictions.
        </div>
      )}

      <div className="space-y-5">
        {packages.map((pkg) => (
          <div key={pkg.stake_id}
            className={`rounded-2xl border bg-[#0B120E] p-4 transition-shadow ${
              highlightPkg === pkg.stake_id
                ? "border-[#1CFD4C]/60 ring-2 ring-[#1CFD4C]/30 shadow-[0_0_24px_rgba(28,253,76,0.15)]"
                : "border-white/10"
            }`}
            data-testid={`pred-package-${pkg.stake_id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg bg-[#1CFD4C]/12 border border-[#1CFD4C]/30 grid place-items-center shrink-0">
                  <Layers className="w-4 h-4 emerald-text" />
                </div>
                <div>
                  <div className="text-sm font-display font-bold text-white">{pkg.tier_name}</div>
                  <div className="text-[10px] text-muted-foreground font-num">
                    {fmtUsd(pkg.principal_usd)} staked · {pkg.answered}/{pkg.required_per_day} answered today
                  </div>
                </div>
              </div>
              <BonusPill status={pkg.bonus_status} required={pkg.required_per_day} bonus={pkg.bonus_usd} testid={`pred-bonus-${pkg.stake_id}`} />
            </div>

            <div className="space-y-2">
              {(pkg.slots ?? []).map((s) => (
                <div key={s.slot} className="rounded-xl border border-white/10 bg-[#090E0B] p-3.5" data-testid={`pred-slot-${pkg.stake_id}-${s.slot}`}>
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-0.5">Question {s.slot + 1}</div>
                      <div className="text-sm text-white leading-snug">{s.question}</div>
                      <div className="text-[10px] text-muted-foreground mt-1">Yes {Math.round(s.yes_price * 100)}% · No {Math.round(s.no_price * 100)}%</div>
                    </div>
                    <StatusChip status={s.status} answered={s.answered_side} correct={s.correct} winning={s.winning_side} />
                  </div>
                  {s.answered_side && s.correct === null && (
                    <div className="text-[10px] text-[#1CFD4C] mt-1.5">Awaiting Polymarket result…</div>
                  )}
                  {s.answered_side && s.correct === false && s.winning && (
                    <div className="text-[10px] text-red-400 mt-1.5">Resolved {s.winning.toUpperCase()} — your pick missed</div>
                  )}
                  {s.status === "open" && (
                    <div className="text-[10px] text-[#1CFD4C] mt-1.5" data-testid={`pred-countdown-${pkg.stake_id}-${s.slot}`}>Closes in {minsLeft(s.closes)}m</div>
                  )}
                  {s.status === "open" && !s.answered_side && (
                    <div className="flex gap-2 mt-3">
                      <button onClick={() => answer(pkg, s.slot, "yes")} disabled={busy} data-testid={`pred-yes-${pkg.stake_id}-${s.slot}`}
                        className="flex-1 rounded-lg bg-[#10B981]/15 border border-[#10B981]/40 emerald-text py-2 text-sm font-semibold hover:bg-[#10B981]/25 transition-colors flex items-center justify-center gap-1">
                        <TrendingUp className="w-3.5 h-3.5" /> Yes</button>
                      <button onClick={() => answer(pkg, s.slot, "no")} disabled={busy} data-testid={`pred-no-${pkg.stake_id}-${s.slot}`}
                        className="flex-1 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 py-2 text-sm font-semibold hover:bg-red-500/25 transition-colors flex items-center justify-center gap-1">
                        <TrendingDown className="w-3.5 h-3.5" /> No</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </Panel>
  );
};

function BonusPill({ status, required, bonus, testid }) {
  const map = {
    earned: { tone: "emerald", text: `Bonus earned${bonus ? ` +${fmtUsd(bonus)}` : ""}` },
    forfeited: { tone: "danger", text: "Bonus forfeited today" },
    in_progress: { tone: "gold", text: `Answer all ${required} → +${fmtUsd(bonus || 0)}` },
  };
  const c = map[status] || map.in_progress;
  return (
    <Pill tone={c.tone} data-testid={testid || "prediction-bonus-status"}>
      <Gift className="w-3 h-3" /> {c.text}
    </Pill>
  );
}

function StatusChip({ status, answered, correct, winning }) {
  if (answered) {
    if (correct === true) return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] emerald-text" data-testid="pred-chip-won"><CheckCircle2 className="w-3.5 h-3.5" /> WON</span>;
    if (correct === false) return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-red-400" data-testid="pred-chip-lost"><XCircle className="w-3.5 h-3.5" /> LOST</span>;
    return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#1CFD4C]" data-testid="pred-chip-pending"><Clock className="w-3.5 h-3.5" /> {answered.toUpperCase()} · PENDING</span>;
  }
  if (status === "open") return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#1CFD4C]"><Clock className="w-3.5 h-3.5" /> Open</span>;
  if (status === "closed") return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-red-400"><XCircle className="w-3.5 h-3.5" /> Missed</span>;
  return <span className="shrink-0 text-[11px] text-muted-foreground">Upcoming</span>;
}
